import React, { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useTranslation } from "@/lib/i18n";
import { fmtCurrency, fmtPercent, fmtNumber, fmtScore } from "@/components/portfolio/_format";
import InfoTooltip from "@/components/portfolio/InfoTooltip";
import HoldingActionMenu from "@/components/portfolio/HoldingActionMenu";
import { useCompanyIdentities } from "@/lib/useCompanyIdentities";
import CompanyLogo from "@/components/common/CompanyLogo";

/** Inline column-header label + (i) tooltip. Wrapping in a flex keeps the
 *  tooltip trigger adjacent to the label and inherits the cell's text-align. */
function HeaderWithTooltip({ label, tooltipText, testId, align = "left" }) {
  const justify = align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start";
  return (
    <span className={`inline-flex items-center gap-1.5 ${justify}`}>
      <span>{label}</span>
      {tooltipText ? (
        <InfoTooltip text={tooltipText} testId={testId} label={label} />
      ) : null}
    </span>
  );
}

function riskBadge(risk, t) {
  const r = (risk || "").toLowerCase();
  const tone = {
    low: "text-[#2BFF8A] border-[#2BFF8A]/40 bg-[#2BFF8A]/10",
    medium: "text-[#F0A93B] border-[#F0A93B]/40 bg-[#F0A93B]/10",
    high: "text-[#FF3DA6] border-[#FF3DA6]/40 bg-[#FF3DA6]/10",
  };
  if (!r) return <span className="text-muted-foreground text-xs">{t("portfolio.common.pending")}</span>;
  return (
    <Badge variant="outline" className={`capitalize border ${tone[r] || tone.medium}`}>
      {t(`portfolio.risk.${r}`)}
    </Badge>
  );
}

function PnLCell({ value, pct, currency = "USD" }) {
  if (value === null || value === undefined) return <span className="text-muted-foreground">—</span>;
  const positive = value >= 0;
  return (
    <span className={`tabular ${positive ? "text-[#2BFF8A]" : "text-[#FF3DA6]"}`}>
      {fmtCurrency(value, { currency, signed: true })}
      {pct !== null && pct !== undefined ? (
        <span className="ml-1 text-[11px] opacity-80">({fmtPercent(pct)})</span>
      ) : null}
    </span>
  );
}

export default function HoldingsTable({
  positions,
  onAddPurchase,
  onRecordSale,
  onViewTransactions,
  onEditMetadata,
  onDeleteIncorrect,
  previewMode = false,
  revealedRowCount = 2,
}) {
  const { t } = useTranslation();
  // Phase 8.LP.LR — batched, cache-only identity lookup for the
  // holdings tickers. Single GET /api/companies/identity request
  // for the full holdings set; falls back to initials when a
  // ticker isn't yet cached (no FMP call is ever triggered from
  // the Portfolio page).
  const holdingTickers = useMemo(
    () => (Array.isArray(positions) ? positions.map((p) => p.ticker) : []),
    [positions]
  );
  const { getIdentity: getHoldingIdentity } = useCompanyIdentities(holdingTickers);
  if (!positions || positions.length === 0) return null;

  // Phase 8.WLNAV.v1.5 — In preview mode, we blur the CELL CONTENTS of
  // every row after `revealedRowCount` (default 2). Row borders, hover
  // states, and structural layout stay sharp so the table still reads
  // as a real, populated portfolio.
  const isBlurred = (idx) => previewMode && idx >= revealedRowCount;
  const cellBlurClass = "[filter:blur(6px)] select-none pointer-events-none";

  return (
    <>
      {/* Desktop table */}
      <div
        data-testid="portfolio-holdings-table-card"
        className="hidden md:block rounded-2xl border border-white/10 bg-[rgba(18,18,23,0.85)] shadow-[var(--app-shadow-elev-1)] overflow-hidden"
      >
        <Table data-testid="portfolio-holdings-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent border-white/10">
              <TableHead>{t("portfolio.table.ticker")}</TableHead>
              <TableHead>{t("portfolio.table.company")}</TableHead>
              <TableHead className="text-right">
                <HeaderWithTooltip
                  label={t("portfolio.table.shares")}
                  tooltipText={t("portfolio.tooltip.shares")}
                  testId="portfolio-table-header-shares-tooltip"
                  align="right"
                />
              </TableHead>
              <TableHead className="text-right">
                <HeaderWithTooltip
                  label={t("portfolio.table.buy_price")}
                  tooltipText={t("portfolio.tooltip.average_buy_price")}
                  testId="portfolio-table-header-buy-price-tooltip"
                  align="right"
                />
              </TableHead>
              <TableHead className="text-right">{t("portfolio.table.current_price")}</TableHead>
              <TableHead className="text-right">{t("portfolio.table.market_value")}</TableHead>
              <TableHead className="text-right">{t("portfolio.table.pnl")}</TableHead>
              <TableHead className="text-right">
                <HeaderWithTooltip
                  label={t("portfolio.table.weight")}
                  tooltipText={t("portfolio.tooltip.position_weight")}
                  testId="portfolio-table-header-weight-tooltip"
                  align="right"
                />
              </TableHead>
              <TableHead className="text-right">
                <HeaderWithTooltip
                  label={t("portfolio.table.fair_value")}
                  tooltipText={t("portfolio.tooltip.fair_value")}
                  testId="portfolio-table-header-fair-value-tooltip"
                  align="right"
                />
              </TableHead>
              <TableHead className="text-right">{t("portfolio.table.upside")}</TableHead>
              <TableHead className="text-right">
                <HeaderWithTooltip
                  label={t("portfolio.table.ai_score")}
                  tooltipText={t("portfolio.tooltip.ai_score")}
                  testId="portfolio-table-header-ai-score-tooltip"
                  align="right"
                />
              </TableHead>
              <TableHead>
                <HeaderWithTooltip
                  label={t("portfolio.table.risk")}
                  tooltipText={t("portfolio.tooltip.risk_level")}
                  testId="portfolio-table-header-risk-tooltip"
                />
              </TableHead>
              <TableHead className="text-right">{t("portfolio.table.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {positions.map((p, idx) => {
              const cur = p.currency || "USD";
              const blurred = isBlurred(idx);
              const blur = blurred ? cellBlurClass : "";
              const ariaHidden = blurred ? "true" : undefined;
              const holdIdent = getHoldingIdentity(p.ticker);
              return (
              <TableRow key={p.holding_id} data-testid={`portfolio-holdings-row-${p.ticker}`} data-preview-blurred={blurred ? "true" : undefined} className="border-white/10 hover:bg-white/[0.03]">
                <TableCell className="font-mono font-medium">
                  <span aria-hidden={ariaHidden} className={`inline-flex items-center gap-2 ${blur}`}>
                    {/* Phase 8.LP.LR — small logo tile before the
                        ticker. Preview mode blurs the whole cell
                        (including the logo) to keep the paywall
                        contract consistent. */}
                    <CompanyLogo
                      ticker={p.ticker}
                      name={holdIdent.name || p.company_name}
                      logoUrl={holdIdent.logo_url}
                      size="xs"
                      testId={`portfolio-holding-logo-${p.ticker}`}
                    />
                    {p.ticker}
                  </span>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-[180px] truncate" title={p.company_name || ""}><span aria-hidden={ariaHidden} className={blur}>{p.company_name || "—"}</span></TableCell>
                <TableCell className="text-right tabular"><span aria-hidden={ariaHidden} className={blur}>{fmtNumber(p.shares, { digits: 4 })}</span></TableCell>
                <TableCell className="text-right tabular"><span aria-hidden={ariaHidden} className={blur}>{fmtCurrency(p.buy_price, { currency: cur })}</span></TableCell>
                <TableCell className="text-right tabular"><span aria-hidden={ariaHidden} className={blur}>{fmtCurrency(p.current_price, { currency: cur, ifNull: "—" })}</span></TableCell>
                <TableCell className="text-right tabular"><span aria-hidden={ariaHidden} className={blur}>{fmtCurrency(p.market_value, { currency: cur, ifNull: "—" })}</span></TableCell>
                <TableCell className="text-right"><span aria-hidden={ariaHidden} className={blur}><PnLCell value={p.unrealized_pnl} pct={p.unrealized_pnl_pct} currency={cur} /></span></TableCell>
                <TableCell className="text-right tabular text-muted-foreground"><span aria-hidden={ariaHidden} className={blur}>{p.portfolio_weight === null || p.portfolio_weight === undefined ? "—" : fmtPercent(p.portfolio_weight)}</span></TableCell>
                <TableCell className="text-right tabular"><span aria-hidden={ariaHidden} className={blur}>{fmtCurrency(p.fair_value, { currency: cur, ifNull: t("portfolio.common.pending") })}</span></TableCell>
                <TableCell className="text-right tabular">
                  <span aria-hidden={ariaHidden} className={blur}>
                  {p.upside_downside_pct === null || p.upside_downside_pct === undefined ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    <span className={p.upside_downside_pct >= 0 ? "text-[#2BFF8A]" : "text-[#FF3DA6]"}>
                      {p.upside_downside_pct >= 0 ? "+" : ""}{fmtPercent(p.upside_downside_pct)}
                    </span>
                  )}
                  </span>
                </TableCell>
                <TableCell className="text-right tabular"><span aria-hidden={ariaHidden} className={blur}>{fmtScore(p.ai_score, { ifNull: "—", digits: 0 })}</span></TableCell>
                <TableCell><span aria-hidden={ariaHidden} className={blur}>{riskBadge(p.risk_level, t)}</span></TableCell>
                <TableCell className="text-right">
                  <div className={`flex items-center justify-end ${previewMode ? "pointer-events-none opacity-70" : ""}`} aria-hidden={previewMode ? "true" : undefined}>
                    <HoldingActionMenu
                      position={p}
                      onAddPurchase={previewMode ? undefined : onAddPurchase}
                      onRecordSale={previewMode ? undefined : onRecordSale}
                      onViewTransactions={previewMode ? undefined : onViewTransactions}
                      onEditMetadata={previewMode ? undefined : onEditMetadata}
                      onDeleteIncorrect={previewMode ? undefined : onDeleteIncorrect}
                    />
                  </div>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {/* Mobile card list */}
      <div
        data-testid="portfolio-holdings-mobile-list"
        className="md:hidden grid gap-3"
      >
        {positions.map((p, idx) => {
          const cur = p.currency || "USD";
          const blurred = isBlurred(idx);
          const blur = blurred ? cellBlurClass : "";
          const ariaHidden = blurred ? "true" : undefined;
          const holdIdent = getHoldingIdentity(p.ticker);
          return (
          <div
            key={p.holding_id}
            data-testid={`portfolio-holding-card-${p.ticker}`}
            data-preview-blurred={blurred ? "true" : undefined}
            className="rounded-xl bg-card/60 backdrop-blur-xl border border-white/10 p-4"
          >
            <div className="flex items-center justify-between gap-3">
              <div className={`flex items-center gap-2.5 min-w-0 ${blur}`} aria-hidden={ariaHidden}>
                {/* Phase 8.LP.LR — logo tile on the left. */}
                <CompanyLogo
                  ticker={p.ticker}
                  name={holdIdent.name || p.company_name}
                  logoUrl={holdIdent.logo_url}
                  size="sm"
                  testId={`portfolio-holding-mobile-logo-${p.ticker}`}
                />
                <div className="min-w-0">
                  <div className="font-mono text-base">{p.ticker}</div>
                  <div className="text-xs text-muted-foreground truncate max-w-[180px]">{p.company_name || "—"}</div>
                </div>
              </div>
              <div className="text-right">
                <div className={`tabular text-sm ${blur}`} aria-hidden={ariaHidden}>{fmtCurrency(p.market_value, { currency: cur, ifNull: "—" })}</div>
                <span aria-hidden={ariaHidden} className={blur}><PnLCell value={p.unrealized_pnl} pct={p.unrealized_pnl_pct} currency={cur} /></span>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <div className="text-muted-foreground inline-flex items-center gap-1">
                <span>{t("portfolio.table.shares")}</span>
                <InfoTooltip text={t("portfolio.tooltip.shares")} testId={`portfolio-holding-mobile-${p.ticker}-shares-tooltip`} label={t("portfolio.table.shares")} />
              </div>
              <div className={`text-right tabular ${blur}`} aria-hidden={ariaHidden}>{fmtNumber(p.shares, { digits: 4 })}</div>
              <div className="text-muted-foreground inline-flex items-center gap-1">
                <span>{t("portfolio.table.buy_price")}</span>
                <InfoTooltip text={t("portfolio.tooltip.average_buy_price")} testId={`portfolio-holding-mobile-${p.ticker}-buy-price-tooltip`} label={t("portfolio.table.buy_price")} />
              </div>
              <div className={`text-right tabular ${blur}`} aria-hidden={ariaHidden}>{fmtCurrency(p.buy_price, { currency: cur })}</div>
              <div className="text-muted-foreground inline-flex items-center gap-1">
                <span>{t("portfolio.table.fair_value")}</span>
                <InfoTooltip text={t("portfolio.tooltip.fair_value")} testId={`portfolio-holding-mobile-${p.ticker}-fair-value-tooltip`} label={t("portfolio.table.fair_value")} />
              </div>
              <div className={`text-right tabular ${blur}`} aria-hidden={ariaHidden}>{fmtCurrency(p.fair_value, { currency: cur, ifNull: t("portfolio.common.pending") })}</div>
              <div className="text-muted-foreground">{t("portfolio.table.upside")}</div>
              <div className={`text-right tabular ${blur}`} aria-hidden={ariaHidden}>
                {p.upside_downside_pct === null || p.upside_downside_pct === undefined
                  ? "—"
                  : `${p.upside_downside_pct >= 0 ? "+" : ""}${fmtPercent(p.upside_downside_pct)}`}
              </div>
              <div className="text-muted-foreground inline-flex items-center gap-1">
                <span>{t("portfolio.table.ai_score")}</span>
                <InfoTooltip text={t("portfolio.tooltip.ai_score")} testId={`portfolio-holding-mobile-${p.ticker}-ai-score-tooltip`} label={t("portfolio.table.ai_score")} />
              </div>
              <div className={`text-right tabular ${blur}`} aria-hidden={ariaHidden}>{fmtScore(p.ai_score, { digits: 0 })}</div>
              <div className="text-muted-foreground inline-flex items-center gap-1">
                <span>{t("portfolio.table.risk")}</span>
                <InfoTooltip text={t("portfolio.tooltip.risk_level")} testId={`portfolio-holding-mobile-${p.ticker}-risk-tooltip`} label={t("portfolio.table.risk")} />
              </div>
              <div className="text-right" aria-hidden={ariaHidden}><span className={blur}>{riskBadge(p.risk_level, t)}</span></div>
            </div>
            <div className={`pt-3 ${previewMode ? "pointer-events-none opacity-70" : ""}`} aria-hidden={previewMode ? "true" : undefined}>
              <HoldingActionMenu
                position={p}
                variant="mobile"
                onAddPurchase={previewMode ? undefined : onAddPurchase}
                onRecordSale={previewMode ? undefined : onRecordSale}
                onViewTransactions={previewMode ? undefined : onViewTransactions}
                onEditMetadata={previewMode ? undefined : onEditMetadata}
                onDeleteIncorrect={previewMode ? undefined : onDeleteIncorrect}
              />
            </div>
          </div>
          );
        })}
      </div>
    </>
  );
}
